import type { BridgeMap, CompatibilityClass, ThreadColumn, ThreadRow, ThreadSelectionState } from "./types";

export interface ClassifyCompatibilityInput {
  row: ThreadRow;
  column: ThreadColumn;
  selectedRow: ThreadRow | null;
  selection: ThreadSelectionState;
  bridges: BridgeMap;
}

function hasBridge(
  bridges: BridgeMap,
  columnId: string,
  leftFamily: string,
  rightFamily: string,
): boolean {
  const pairs = bridges[columnId] ?? [];
  return pairs.some(([a, b]) =>
    (a === leftFamily && b === rightFamily) ||
    (a === rightFamily && b === leftFamily),
  );
}

export function classifyCompatibility({
  row,
  column,
  selectedRow,
  selection,
  bridges,
}: ClassifyCompatibilityInput): CompatibilityClass {
  const primary = selection.primary;
  if (primary.kind !== "cell" || !selectedRow) return "neutral";
  if (row.runId === primary.rowId && column.id === primary.colId) return "selected";

  const sameFamily = row.family === selectedRow.family;
  const sameLineage =
    sameFamily &&
    row.design === selectedRow.design &&
    row.mesh === selectedRow.mesh &&
    row.script === selectedRow.script;

  if (sameLineage) return "connected";

  const sameQuantity = column.kind === "quantity" && column.id === primary.colId;
  if (!sameQuantity) return "adjacent";

  return hasBridge(bridges, column.id, row.family, selectedRow.family)
    ? "compatible"
    : "incompatible";
}

export function explainCompatibility(kind: CompatibilityClass): string {
  switch (kind) {
    case "selected":
      return "Selected value: reveal what made it and what depends on it.";
    case "connected":
      return "Directly connected: same DAG lineage and model family.";
    case "compatible":
      return "Pivot-compatible: different lineage, but a bridge says this value is safe to compare.";
    case "adjacent":
      return "Adjacent only: nearby in the projection, not proven related.";
    case "incompatible":
      return "Not compatible: no known bridge supports this comparison.";
    case "neutral":
      return "No active comparison.";
  }
}
